import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategories } from '../store/slices/productSlice';
import Loader from '../components/common/loader';

const Categories = () => {
  const dispatch = useDispatch();
  const { categories, loading, error } = useSelector(state => state.products);
  
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Helper function to format category name
  const formatCategoryName = (category) => {
    return category
      .replace(/_/g, ' ')
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to get background gradient based on category
  const getCategoryGradient = (category) => {
    const gradients = {
      'AGRICULTURE_PRODUCTS': 'from-green-100 to-emerald-100',
      'PACKAGING_PRODUCTS': 'from-blue-100 to-indigo-100',
    };
    
    return gradients[category] || 'from-gray-100 to-gray-200';
  };

  // Helper function to get icon based on category
  const getCategoryIcon = (category) => {
    const icons = {
      'AGRICULTURE_PRODUCTS': '🌾',
      'PACKAGING_PRODUCTS': '📦',
    };
    
    return icons[category] || '📦';
  };

  // Helper function to get description based on category
  const getCategoryDescription = (category) => {
    const descriptions = {
      'AGRICULTURE_PRODUCTS': 'High-quality films, covers, and nets for modern agriculture',
      'PACKAGING_PRODUCTS': 'Professional packaging solutions for all your business needs',
    };
    
    return descriptions[category] || 'Explore our product collection';
  };

  // Helper function to get subcategories for display
  const getCategorySubcategories = (category) => {
    const subcategories = {
      'AGRICULTURE_PRODUCTS': [
        'Mulching Film'
      ],
      'PACKAGING_PRODUCTS': [
        'Courier Bags','Paper Bags'
      ],
    };
    
    return subcategories[category] || [];
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Product Categories</h1>
        <p className="text-gray-600">Browse our extensive collection of agriculture and packaging products</p>
      </div>
      
      {loading.categories ? (
        <div className="flex justify-center items-center h-64">
          <Loader size="lg" />
        </div>
      ) : error.categories ? (
        <div className="bg-red-100 text-red-700 p-4 rounded">
          {error.categories}
        </div>
      ) : !categories || categories.length === 0 ? (
        <div className="bg-yellow-100 text-yellow-800 p-4 rounded">
          No categories found.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {categories.map((category) => (
            <Link 
              key={category._id} 
              to={`/category/${category._id}`}
              className="block group"
            >
              <div className={`rounded-xl shadow-lg p-8 bg-gradient-to-br ${getCategoryGradient(category._id)} hover:shadow-xl transition duration-300 transform hover:-translate-y-1`}>
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <span className="text-5xl" role="img" aria-label={category._id}>
                      {getCategoryIcon(category._id)}
                    </span>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800 group-hover:text-blue-600 transition-colors">
                        {formatCategoryName(category._id)}
                      </h3>
                      <span className="inline-block mt-1 bg-white text-gray-700 text-sm font-medium px-3 py-1 rounded-full shadow-sm">
                        {category.count} {category.count === 1 ? 'product' : 'products'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Description */}
                <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                  {getCategoryDescription(category._id)}
                </p>
                
                {/* Subcategories */}
                {getCategorySubcategories(category._id).length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-600 mb-2">Popular Subcategories:</p>
                    <div className="flex flex-wrap gap-2">
                      {getCategorySubcategories(category._id).slice(0, 6).map((sub, idx) => (
                        <span 
                          key={idx}
                          className="text-xs bg-white bg-opacity-70 text-gray-700 px-2 py-1 rounded"
                        >
                          {sub}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* CTA */}
                <div className="mt-6 flex items-center text-blue-600 font-semibold group-hover:text-blue-700 transition-colors">
                  <span>Browse Category</span>
                  <svg 
                    className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>

                {/* Features */}
                <div className="mt-6 pt-6 border-t border-gray-300 border-opacity-50">
                  <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Quality Products</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Bulk Orders</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Fast Delivery</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Best Prices</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Additional Info Section */}
      <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8">
        <h2 className="text-2xl font-bold mb-4">Why Choose Our Products?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <div className="bg-blue-600 text-white rounded-full p-2 flex-shrink-0">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Premium Quality</h3>
              <p className="text-sm text-gray-600">All products meet international quality standards</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-green-600 text-white rounded-full p-2 flex-shrink-0">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Competitive Pricing</h3>
              <p className="text-sm text-gray-600">Best prices with bulk discounts available</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-purple-600 text-white rounded-full p-2 flex-shrink-0">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Fast Delivery</h3>
              <p className="text-sm text-gray-600">Quick shipping across all major cities</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Categories;